import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS } from "../../data/landing";

const PhoneFrame = ({ src, alt, className, testid }) => (
  <div
    className={`relative rounded-[2.5rem] bg-[#2A1112] p-2 shadow-[0_40px_80px_-24px_rgba(123,17,19,0.35)] ${className}`}
    data-testid={testid}
  >
    {/* notch */}
    <div className="absolute left-1/2 top-3 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-[#2A1112]" />
    <div className="overflow-hidden rounded-[2rem] bg-white">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="block w-full h-auto select-none"
        draggable={false}
      />
    </div>
  </div>
);

export const PhoneMockups = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  // Parallax: side phones move slower/faster than center
  const yLeft = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const yRight = useTransform(scrollYProgress, [0, 1], [90, -40]);
  const yCenter = useTransform(scrollYProgress, [0, 1], [20, -20]);

  return (
    <div
      ref={ref}
      className="relative mx-auto mt-4 flex h-[440px] w-full max-w-[340px] items-center justify-center sm:h-[520px] sm:max-w-2xl lg:h-[600px] lg:max-w-4xl"
      data-testid="hero-phone-mockups"
    >
      {/* soft glow behind */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[300px] w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#F5A623]/25 blur-[90px] sm:h-[420px] sm:w-[420px]" />

      {/* Left phone */}
      <motion.div
        style={{ y: yLeft }}
        initial={{ opacity: 0, x: 40, rotate: 0 }}
        animate={{ opacity: 1, x: 0, rotate: -9 }}
        transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="absolute left-0 z-10 w-[52%] sm:w-[42%] lg:w-[40%]"
      >
        <motion.div
          animate={{ y: [-8, 8, -8] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        >
          <PhoneFrame
            src={ASSETS.screenAstro}
            alt="Verified astrologers list in the Hari Om Bol app"
            className="scale-90 opacity-95"
            testid="phone-left"
          />
        </motion.div>
      </motion.div>

      {/* Right phone */}
      <motion.div
        style={{ y: yRight }}
        initial={{ opacity: 0, x: -40, rotate: 0 }}
        animate={{ opacity: 1, x: 0, rotate: 9 }}
        transition={{ duration: 1, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
        className="absolute right-0 z-10 w-[52%] sm:w-[42%] lg:w-[40%]"
      >
        <motion.div
          animate={{ y: [8, -8, 8] }}
          transition={{ duration: 7.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <PhoneFrame
            src={ASSETS.screenSaathi}
            alt="Saathi companion screen in the Hari Om Bol app"
            className="scale-90 opacity-95"
            testid="phone-right"
          />
        </motion.div>
      </motion.div>

      {/* Center phone (largest) */}
      <motion.div
        style={{ y: yCenter }}
        initial={{ opacity: 0, y: 60, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="absolute z-20 w-[62%] sm:w-[46%] lg:w-[44%]"
      >
        <motion.div
          animate={{ y: [-12, 12, -12] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        >
          <PhoneFrame
            src={ASSETS.screenHome}
            alt="Hari Om Bol app home screen with services"
            className=""
            testid="phone-center"
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PhoneMockups;
